// frontend/src/features/regions/components/RegionsClientsChart.tsx
import React from 'react';
import {
    BarChart, Bar, Cell, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, LabelList
} from 'recharts';
import { ClientData } from '../services/regionsAPI';

interface RegionsClientsChartProps {
    data: ClientData[];
    grandTotalVenda: number;
    onClientClick?: (clientName: string) => void;
    selectedClientName?: string | null;
    highlightedClientNames?: string[];
}

const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
// Use the exact CSS variable from the dashboard top clients chart
const BAR_COLOR = 'var(--theme-accent, #1e293b)';

const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', {
        style: 'currency', currency: 'BRL',
        notation: 'compact', compactDisplay: 'short',
    }).format(value);

const BarTick = (props: any) => {
    const { x, y, payload } = props;
    const raw = String(payload.value ?? '');
    const maxLen = 18;
    const display = raw.length > maxLen ? raw.substring(0, maxLen - 2) + '…' : raw;
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={-8}
                y={0}
                dy="0.35em"
                textAnchor="end"
                fontSize={11}
                fontWeight={700}
                fill="var(--text-primary, #111827)"
                style={{ textTransform: 'uppercase', fontFamily: FONT }}
            >
                {display}
            </text>
        </g>
    );
};

const CustomTooltip = ({ active, payload, grandTotalVenda }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload as ClientData;
    const mcPercent = d.totalVenda > 0 ? (d.totalMargem / d.totalVenda) * 100 : 0;
    const contrib = d.participacao
        ? d.participacao * 100
        : grandTotalVenda > 0 ? (d.totalVenda / grandTotalVenda) * 100 : 0;

    return (
        <div style={{
            background: 'var(--bg-widget)', borderRadius: 'var(--radius-base, 8px)', padding: '10px 14px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.12)', border: '1px solid var(--border-primary)',
            fontSize: 13, maxWidth: 280, fontFamily: FONT,
        }}>
            <p style={{ fontWeight: 700, color: 'var(--text-primary)', margin: '0 0 8px', fontSize: 14,
                borderBottom: '1px solid var(--border-primary)', paddingBottom: 6 }}>
                {d.cliente}
            </p>
            {[
                { label: 'Vendas', value: formatCurrency(d.totalVenda), color: 'var(--text-primary)' },
                { label: 'Região (UF)', value: d.uf, color: 'var(--text-primary)' },
                { label: 'M.C. %', value: `${mcPercent.toFixed(2)}%`, color: '#16a34a' },
                { label: 'Contribuição', value: `${contrib.toFixed(2)}%`, color: '#2563eb' },
            ].map(({ label, value, color }) => (
                <div key={label} style={{ display: 'flex', justifyContent: 'space-between',
                    gap: 16, padding: '2px 0' }}>
                    <span style={{ color: 'var(--text-secondary)', fontWeight: 500 }}>{label}</span>
                    <span style={{ fontWeight: 700, color }}>{value}</span>
                </div>
            ))}
        </div>
    );
};

const RegionsClientsChart: React.FC<RegionsClientsChartProps> = ({ data, grandTotalVenda, onClientClick, selectedClientName, highlightedClientNames }) => {
    if (!data?.length) {
        return <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8',
            fontWeight: 600, fontSize: 13, fontFamily: FONT }}>Nenhum cliente encontrado.</div>;
    }

    const chartData = [...data]
        .sort((a, b) => b.totalVenda - a.totalVenda)
        .slice(0, 10);

    const hasHighlight = highlightedClientNames && highlightedClientNames.length > 0;
    const hasSelection = !!selectedClientName;

    return (
        <ResponsiveContainer width="100%" height="100%">
            <BarChart
                data={chartData}
                layout="vertical"
                margin={{ top: 4, right: 130, left: 0, bottom: 4 }}
                barCategoryGap="25%"
            >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#f0f0f0" />
                <XAxis type="number" hide domain={[0, (max: number) => max * 1.35]} />
                <YAxis
                    dataKey="cliente"
                    type="category"
                    axisLine={false}
                    tickLine={false}
                    width={160}
                    tick={<BarTick />}
                />
                <Tooltip
                    content={<CustomTooltip grandTotalVenda={grandTotalVenda} />}
                    cursor={{ fill: 'rgba(0,0,0,0.03)' }}
                />
                <Bar
                    dataKey="totalVenda"
                    radius={[0, 0, 0, 0]}
                    barSize={16}
                    style={{ cursor: onClientClick ? 'pointer' : 'default' }}
                >
                    {chartData.map((entry, index) => {
                        const isSelected = selectedClientName === entry.cliente;
                        const isHighlighted = hasHighlight && highlightedClientNames!.includes(entry.cliente);
                        const dimmed = hasHighlight
                            ? !isHighlighted && !isSelected
                            : hasSelection && !isSelected;
                        const fill = isSelected || isHighlighted
                            ? 'var(--theme-primary, #b91c1c)'
                            : BAR_COLOR;
                        return (
                            <Cell
                                key={`cell-${index}`}
                                fill={fill}
                                opacity={dimmed ? 0.25 : 1}
                                onClick={() => onClientClick?.(entry.cliente)}
                            />
                        );
                    })}
                    <LabelList
                        dataKey="totalVenda"
                        position="right"
                        content={({ x, y, width, height: h, value }: any) => (
                            <text
                                x={(x as number) + (width as number) + 8}
                                y={(y as number) + (h as number) / 2}
                                textAnchor="start"
                                dominantBaseline="central"
                                fontSize={13}
                                fontWeight={700}
                                fill="var(--text-primary, #111827)"
                                style={{ pointerEvents: 'none', fontFamily: FONT }}
                            >
                                {formatCurrency(value)}
                            </text>
                        )}
                    />
                </Bar>
            </BarChart>
        </ResponsiveContainer>
    );
};

export default RegionsClientsChart;
